import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button, Empty, Tag, Typography } from "antd";
import {
  CloseOutlined,
  LinkOutlined,
  RightOutlined,
  UserOutlined
} from "@ant-design/icons";
import {
  LOD_LABELS,
  type Classification,
  type DsmScopeItem
} from "design-scope";
import type { ProjectEntities } from "../hooks/use-project-entities";
import { ClassBadge, LodMeter } from "./scope-ui";
import "./scope-item.scss";

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4 last:mb-0">
      <Typography.Text type="secondary" style={{ fontSize: 13 }}>
        {label}
      </Typography.Text>
      <div className="mt-1 break-words" style={{ fontSize: 15 }}>
        {children}
      </div>
    </div>
  );
}

/** One block of the panel, ruled off from the next as in the mockup. */
function PanelSection({ children }: { children: ReactNode }) {
  return (
    <div
      className="px-4 py-4"
      style={{ borderBottom: "1px solid var(--ciq-gray-200)" }}
    >
      {children}
    </div>
  );
}

function ClassificationDetail({
  label,
  c
}: {
  label: string;
  c?: Classification;
}) {
  if (!c) return null;
  return (
    <Detail label={label}>
      <span className="inline-flex flex-wrap items-center gap-2">
        <Typography.Text strong>{c.title}</Typography.Text>
        {c.code && <ClassBadge system={c.system} code={c.code} />}
      </span>
    </Detail>
  );
}

/** A linked-package block: heading with a Link action, cards or an empty state. */
function LinkedSection({
  title,
  count,
  onLink,
  testId,
  children
}: {
  title: string;
  count: number;
  onLink?: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div data-testid={testId}>
      <div className="mb-2 flex items-center justify-between">
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {title}
        </Typography.Text>
        {onLink && (
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            data-testid={`${testId}-link`}
            onClick={onLink}
          >
            Link
          </Button>
        )}
      </div>
      {count ? (
        <div className="flex flex-col gap-2">{children}</div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No package linked yet"
          className="!my-2"
        />
      )}
    </div>
  );
}

function CardHeading({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-between gap-2"
    >
      <span className="flex min-w-0 items-center gap-2">{children}</span>
      <RightOutlined style={{ fontSize: 11, color: "var(--ciq-gray-500)" }} />
    </Link>
  );
}

export default function ScopeDetailPanel(props: {
  item: DsmScopeItem | null;
  projectId: string;
  entities: ProjectEntities;
  /** Omitted for read-only (archived) projects; hides Edit and Link. */
  onEdit?: (item: DsmScopeItem) => void;
  onClose: () => void;
}) {
  const { item, projectId, entities, onEdit, onClose } = props;

  if (!item)
    return (
      <div className="flex h-full items-center justify-center p-10 text-center">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Select a scope item to see its design packages, bids, and status"
        />
      </div>
    );

  const { uf, mf, label } = item.classification ?? {};
  const edit = onEdit ? () => onEdit(item) : undefined;

  return (
    <div className="flex h-full flex-col bg-white" data-testid="scope-detail">
      <div
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid var(--ciq-gray-200)" }}
      >
        <Typography.Text strong>Scope Item Details</Typography.Text>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label="Close details"
          data-testid="scope-detail-close"
          onClick={onClose}
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <PanelSection>
          <Detail label="Title">
            <Typography.Text strong style={{ fontSize: 16 }}>
              {item.title || uf?.title || mf?.title || label?.title || "—"}
            </Typography.Text>
          </Detail>
          {item.description && (
            <Detail label="Description">
              <Typography.Paragraph className="!mb-0">
                {item.description}
              </Typography.Paragraph>
            </Detail>
          )}
          <Detail label="Responsibility">
            {item.responsibility ? (
              <span className="inline-flex items-center gap-1">
                <UserOutlined />
                {item.responsibility}
              </span>
            ) : (
              <Typography.Text type="secondary">
                Edit to add responsibility
              </Typography.Text>
            )}
          </Detail>
        </PanelSection>
        {(mf || uf || label) && (
          <PanelSection>
            <ClassificationDetail label="Master Format" c={mf} />
            <ClassificationDetail label="Uni Format" c={uf} />
            <ClassificationDetail label="Custom Format" c={label} />
          </PanelSection>
        )}
        <PanelSection>
          <LinkedSection
            title="Linked Design Package"
            count={item.commitments.length}
            onLink={edit}
            testId="scope-detail-dps"
          >
            {item.commitments.map((c, i) => (
              <div key={i} className="dsm-link-card rounded-lg p-3">
                <CardHeading
                  to={`/project/${projectId}/design-packages/${c.designPackageId}`}
                >
                  <Typography.Text strong ellipsis>
                    {entities.nameOfDp(c.designPackageId)}
                  </Typography.Text>
                </CardHeading>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <Detail label="Milestone">
                    {c.milestoneId
                      ? entities.nameOfMilestone(c.milestoneId)
                      : "—"}
                  </Detail>
                  <Detail label="Level of Detail (LOD)">
                    <LodMeter lod={c.lod} />
                    <div>{LOD_LABELS[c.lod]}</div>
                  </Detail>
                </div>
                {c.notes && (
                  <Detail label="Description">
                    <Typography.Text>{c.notes}</Typography.Text>
                  </Detail>
                )}
              </div>
            ))}
          </LinkedSection>
        </PanelSection>
        <PanelSection>
          <LinkedSection
            title="Linked Bid Package"
            count={item.bids.length}
            onLink={edit}
            testId="scope-detail-bps"
          >
            {item.bids.map((b, i) => (
              <div key={i} className="dsm-link-card rounded-lg px-3 py-2">
                <CardHeading
                  to={`/project/${projectId}/bid-packages/${b.bidPackageId}`}
                >
                  <Typography.Text ellipsis>
                    {entities.nameOfBp(b.bidPackageId)}
                  </Typography.Text>
                  {b.primary && <Tag className="dsm-primary-tag">Primary</Tag>}
                </CardHeading>
              </div>
            ))}
          </LinkedSection>
        </PanelSection>
      </div>

      <div
        className="flex justify-end gap-2 px-4 py-3"
        style={{ borderTop: "1px solid var(--ciq-gray-200)" }}
      >
        <Button onClick={onClose}>Cancel</Button>
        {edit && (
          <Button type="primary" data-testid="scope-detail-edit" onClick={edit}>
            Edit
          </Button>
        )}
      </div>
    </div>
  );
}
